import type { AxisAssessment, LevelAssessmentResult } from "@shared/labs/level-assessment";
import type { WhyData } from "./why-panel";

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/** Adjacent level labels around `level`, clamped to the model range. */
export function adjacentLabels(
  level: number,
  levels: { min: number; max: number },
): { up: string; down: string } {
  return {
    up: `L${clamp(level + 1, levels.min, levels.max)}`,
    down: `L${clamp(level - 1, levels.min, levels.max)}`,
  };
}

/** Build the 3-column why-data for a scored axis. Null when the axis abstained
 *  (the detail view shows missing-evidence instead). Prefers the
 *  explainer-authored lists (`a.explanation.why*` — the single user-facing
 *  voice); falls back per-lane to the scoring-call why* fields for rows
 *  produced before the explainer shipped (or when it failed/omitted a lane). */
export function axisWhyData(
  a: AxisAssessment,
  levels: { min: number; max: number },
): WhyData | null {
  if (a.inferredLevel === null) return null;
  const adj = adjacentLabels(a.inferredLevel, levels);
  const exp = a.explanation;
  const lane = (
    explained: WhyData["satisfied"]["items"] | undefined,
    scoring: WhyData["satisfied"]["items"],
  ) => (explained && explained.length > 0 ? explained : scoring);
  return {
    satisfied: { level: `L${a.inferredLevel}`, items: lane(exp?.whyThisLevel, a.whyThisLevel) },
    up: { level: adj.up, items: lane(exp?.whyNotHigher, a.whyNotHigher) },
    down: { level: adj.down, items: lane(exp?.whyNotLower, a.whyNotLower) },
  };
}

/** Count of scored axes at each level across both sections — the distribution. */
export function levelDistribution(result: LevelAssessmentResult): Map<number, number> {
  const dist = new Map<number, number>();
  for (const a of [...result.cross.axes, ...result.vertical.axes]) {
    if (a.inferredLevel === null) continue;
    dist.set(a.inferredLevel, (dist.get(a.inferredLevel) ?? 0) + 1);
  }
  return dist;
}

/**
 * Synthesize the overall "por que Lx, e não Lx±1" comparative from the per-axis
 * distribution — no extra LLM call. The overall level has no model-emitted
 * counterfactuals, so we derive honest, count-based claims from where the axis
 * mass actually sits.
 */
export function synthesizeOverallWhy(
  result: LevelAssessmentResult,
  levels: { min: number; max: number },
): WhyData | null {
  const assigned = result.overallLevel;
  if (assigned === null) return null;
  const scored = [...result.cross.axes, ...result.vertical.axes].filter(
    (a) => a.inferredLevel !== null,
  );
  const graded = scored.length;
  if (graded === 0) return null;
  const atOrAbove = scored.filter((a) => (a.inferredLevel as number) >= assigned).length;
  const above = scored.filter((a) => (a.inferredLevel as number) > assigned).length;
  const aboveFloor = scored.filter((a) => (a.inferredLevel as number) >= assigned).length;
  const adj = adjacentLabels(assigned, levels);

  return {
    satisfied: {
      level: `L${assigned}`,
      items: [
        {
          point: `O conjunto das competências resulta em L${assigned}.`,
          evidence: `${atOrAbove} de ${graded} competência(s) em L${assigned} ou acima.`,
        },
      ],
    },
    up: {
      level: adj.up,
      items: [
        {
          point: `Não há base para ${adj.up}.`,
          evidence:
            above > 0
              ? `Apenas ${above} de ${graded} competência(s) acima de L${assigned}.`
              : `Nenhuma competência acima de L${assigned}.`,
        },
      ],
    },
    down: {
      level: adj.down,
      items: [
        {
          point: `O conjunto supera ${adj.down}.`,
          evidence: `${aboveFloor} de ${graded} competência(s) em L${assigned} ou acima.`,
        },
      ],
    },
  };
}
